import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import type { LabelColor } from '@pflegenavi/web-components';
import {
  Label,
  Link,
  makeConfig,
  PaginatedTable2,
} from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { GetPayinsResult } from '@pflegenavi/frontend/api-nursing-home';
import { useGetPayinsPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { PendingChargeStatus } from '@pflegenavi/shared/api';
import { YEAR_SHORTMONTH_DAY_FORMAT } from '@pflegenavi/shared/constants';
import { LinkedTableSection } from './LinkedTableSection';
import { useLinkedTablePagination } from './useLinkedTablePagination';

type Payin = GetPayinsResult['data'][number];

type ColumnNames = 'resident' | 'employee' | 'date' | 'amount' | 'status';

const statusLabel = (
  status: Payin['pending_charge_status']
): { color: LabelColor; key: string } => {
  switch (status) {
    case PendingChargeStatus.pending:
      return { color: 'warning', key: 'pending' };
    case PendingChargeStatus.success:
      return { color: 'success', key: 'completed' };
    case PendingChargeStatus.frozen:
      return { color: 'error', key: 'disputed' };
    default:
      return { color: 'error', key: 'failed' };
  }
};

export const PendingChargesTable: FC<{ chargeIds: string[] }> = ({
  chargeIds,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const { selectedNursingHome } = useNursingHomeContext();

  const { page, pageSize, pagination } = useLinkedTablePagination(
    chargeIds.length
  );

  const { data, isLoading } = useGetPayinsPhoenix(
    {
      params: {
        nursing_home_id: selectedNursingHome?.id ?? '',
        page,
        pageSize,
        charge_ids: chargeIds,
      },
    },
    { enabled: selectedNursingHome !== undefined }
  );

  const config = useMemo(
    () =>
      makeConfig<Payin, ColumnNames, never>({
        showAll: undefined,
        hideEmptyRows: true,
        columns: [
          {
            key: 'resident',
            label: t('accounting.payments.table-head.resident'),
            align: 'left',
            component: ({ row }) => (
              <Link
                to={generatePath(nhAppMainPages.RESIDENT_DETAIL_ACCOUNT, {
                  resident_id: row.resident.id,
                })}
              >
                <Typography variant="subtitle2" noWrap>
                  {row.resident.firstname} {row.resident.lastname}
                </Typography>
              </Link>
            ),
          },
          {
            key: 'employee',
            label: t('accounting.payments.table-head.employee'),
            align: 'left',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {row.employee.first_name} {row.employee.last_name}
              </Typography>
            ),
          },
          {
            key: 'date',
            label: t('accounting.payments.table-head.date'),
            align: 'left',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {fDate(row.charge_date, YEAR_SHORTMONTH_DAY_FORMAT)}
              </Typography>
            ),
          },
          {
            key: 'amount',
            label: t('accounting.payments.table-head.amount'),
            align: 'right',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {fCurrencyCents(row.amount)}
              </Typography>
            ),
          },
          {
            key: 'status',
            label: t('accounting.payments.table-head.type'),
            align: 'left',
            component: ({ row }) => {
              const { color, key } = statusLabel(row.pending_charge_status);
              return (
                <Label color={color} variant="ghost">
                  {t(`accounting.payouts.states.${key}`)}
                </Label>
              );
            },
          },
        ],
        pagination,
      }),
    [t, fDate, fCurrencyCents, pagination]
  );

  return (
    <LinkedTableSection
      title={t(
        'cashManagement.group-detail.overview.linked-pending-charges-title'
      )}
      count={chargeIds.length}
    >
      <PaginatedTable2
        name="CashTransactionGroupDetailPendingChargeTable"
        config={config}
        rows={data?.data ?? []}
        isLoading={isLoading}
      />
    </LinkedTableSection>
  );
};
